import type {ChatComposerReadiness} from '@github-ui/agent-sessions-interface/hooks/use-chat-composer-readiness'
import type {RelayReadinessReceipt} from '@github-ui/ahp-relay/relay-readiness'
import type {EstablishedAhpSession, EstablishedAhpSessionViewSnapshot} from '@github-ui/ahp-session-client/contracts'
import {
  captureUiReadinessTime,
  claimCopilotUiNavigation,
  isUiReadinessEnabled,
  localUiReadinessTime,
  type CopilotUiReadinessView,
} from '@github-ui/copilot-browser-latency/copilot-ui-readiness'
import {useCopilotUiReadiness} from '@github-ui/copilot-browser-latency/copilot-ui-readiness-context'
import {useCallback, useEffect, useRef} from 'react'

interface ReadinessWitnessOptions {
  kind: 'new' | 'existing'
  session?: Pick<EstablishedAhpSession, 'identity'>
  snapshot?: EstablishedAhpSessionViewSnapshot
  readOnly: boolean
  statePending?: boolean
  userActionPending?: boolean
  hidden?: boolean
}

function latestReceipt(snapshot: EstablishedAhpSessionViewSnapshot): RelayReadinessReceipt | undefined {
  const {chat, session, role, adopted} = snapshot.readiness ?? {}
  return [chat, session, role, adopted].reduce<RelayReadinessReceipt | undefined>(
    (latest, receipt) => (receipt && (!latest || receipt.monotonicMs > latest.monotonicMs) ? receipt : latest),
    undefined,
  )
}

export function useUiReadinessWitness({
  kind,
  session,
  snapshot,
  readOnly,
  statePending = false,
  userActionPending = false,
  hidden = false,
}: ReadinessWitnessOptions): ((controls: ChatComposerReadiness) => void) | undefined {
  const context = useCopilotUiReadiness()
  const selectedViewRef = useRef<
    | {
        context: CopilotUiReadinessView
        view: CopilotUiReadinessView
        owned: boolean
        unsubscribe: () => void
        userActionPending: boolean
        eligibilityTime?: ReturnType<typeof captureUiReadinessTime>
      }
    | undefined
  >(undefined)
  const committedCheckRef = useRef<(() => void) | undefined>(undefined)
  const mountedRef = useRef(false)

  const onReadiness = useCallback(
    (controls: ChatComposerReadiness) => {
      const current = selectedViewRef.current?.view ?? context
      if (controls.observationError) {
        current?.observationUnavailable(controls.observationError)
        return
      }
      if (hidden || !controls.connected) {
        current?.controlsUnavailable()
        return
      }
      const pending = claimCopilotUiNavigation()
      const owner = context ?? pending ?? selectedViewRef.current?.context
      if (!owner) return
      if (pending || selectedViewRef.current?.context !== owner) {
        const previous = selectedViewRef.current
        previous?.unsubscribe()
        if (previous?.owned) previous.view.dispose('replaced')
        const view = pending ?? owner
        selectedViewRef.current = {
          context: owner,
          view,
          owned: Boolean(pending),
          userActionPending: false,
          unsubscribe: view.subscribeConfiguration(() => committedCheckRef.current?.()),
        }
        pending?.initialized()
      }
      const view = selectedViewRef.current?.view
      if (!view || !view.active || view.ready) return
      view.initialized()

      const check = () => {
        if (!view.active || view.ready) return
        let binding: Parameters<CopilotUiReadinessView['bind']>[0]
        let role: Parameters<CopilotUiReadinessView['stateAvailable']>[1]
        let source: Parameters<CopilotUiReadinessView['stateAvailable']>[2] = 'local'
        let time = view.configurationTime
        if (kind === 'new') {
          binding = {kind: 'new_session'}
          role = readOnly ? 'read_only' : 'writable'
          const selected = selectedViewRef.current
          if (!selected) return
          if (!readOnly && userActionPending) {
            selected.userActionPending = true
            view.dependencyChanged('configuration', true)
            return
          }
          if (selected.userActionPending) {
            const eligible = captureUiReadinessTime()
            if (!eligible) return
            selected.userActionPending = false
            selected.eligibilityTime = eligible
          }
          // Cached configuration alone does not make a user-gated target actionable.
          // Keep the open clock, but adopt state after the required choice or SSO completes.
          if (
            time &&
            selected.eligibilityTime?.clockId === time.clockId &&
            selected.eligibilityTime.monotonicMs > time.monotonicMs
          ) {
            time = selected.eligibilityTime
          }
          const waiting = (!readOnly && statePending) || view.configurationPending || !time
          view.dependencyChanged('configuration', waiting, waiting ? undefined : time)
          if (waiting) return
        } else {
          const waiting = !session || !snapshot?.state || !snapshot.sessionState
          if (waiting || !session || !snapshot?.state || !snapshot.sessionState) {
            view.dependencyChanged('session', true)
            return
          }
          if (snapshot.status !== 'connected' || !snapshot.reachable || snapshot.sessionState.lifecycle !== 'ready') {
            view.dependencyChanged('environment', true)
            return
          }
          if (!snapshot.passiveKnown) {
            view.dependencyChanged('role', true)
            return
          }
          const expectedChat = session.identity.channel?.startsWith('ahp-chat:')
            ? session.identity.channel
            : snapshot.sessionState.defaultChat
          if (!expectedChat || !snapshot.state.resource || !Array.isArray(snapshot.sessionState.chats)) {
            view.dependencyChanged('chat', true)
            return
          }
          if (
            snapshot.state.resource !== expectedChat ||
            !snapshot.sessionState.chats.some(chat => chat.resource === expectedChat)
          ) {
            view.failure('session_mismatch')
            return
          }
          if (snapshot.state.interactivity === 'hidden') return
          const receipt = latestReceipt(snapshot)
          if (!receipt) return
          time = localUiReadinessTime(receipt)
          if (!time) return
          source = receipt.source
          if (time.clockId === view.startedAt.clockId && time.monotonicMs < view.startedAt.monotonicMs) {
            time = view.startedAt
            source = 'cache'
          }
          view.dependencyChanged('environment', false, time)
          view.dependencyChanged('session', false, time)
          view.dependencyChanged('role', false, time)
          view.dependencyChanged('chat', false, time)
          binding = {
            kind: 'session',
            sessionId: session.identity.sessionId,
            environmentId: session.identity.environmentId,
            taskId: session.identity.taskId,
            chatId: expectedChat,
          }
          role = snapshot.passive ? 'passive' : snapshot.state.interactivity === 'read-only' ? 'read_only' : 'writable'
        }
        if (!time) return
        view.bind(binding)
        const stateEventId = view.stateAvailable(binding, role, source, time)
        if (!stateEventId) return
        view.controlsObserved(
          role === 'writable'
            ? {
                binding,
                role,
                connected: controls.connected,
                editable: controls.editable,
                editHandlerAttached: controls.editHandlerAttached,
                actionHandlerAttached: controls.actionHandlerAttached,
                validInputActionEnabled: controls.validInputActionEnabled,
              }
            : {
                binding,
                role,
                connected: controls.connected,
                editable: controls.editable,
                mutationActionsDisabled: controls.mutationActionsDisabled,
              },
          stateEventId,
        )
      }
      committedCheckRef.current = check
      check()
    },
    [context, hidden, kind, readOnly, session, snapshot, statePending, userActionPending],
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (!selectedViewRef.current) return
      selectedViewRef.current.view.controlsUnavailable()
      committedCheckRef.current = undefined
      queueMicrotask(() => {
        if (mountedRef.current) return
        const selected = selectedViewRef.current
        selected?.unsubscribe()
        if (selected?.owned) selected.view.dispose()
      })
    }
  }, [])
  return !hidden && (context || isUiReadinessEnabled()) ? onReadiness : undefined
}
